import { Recipient } from '../value-objects/Recipient';
import { AddressBookEntry } from './AddressBookEntry';

export interface AlertCondition {
  field: string;
  operator: '==' | '!=' | '>' | '>=' | '<' | '<=';
  threshold: number;
}

export interface AlertEmail {
  enabled: boolean;
  emails: string[];
}

export interface AlertSMS {
  enabled: boolean;
  phoneNumbers: string[];
}

export class Alert {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly enabled: boolean,
    public readonly conditions: AlertCondition[],
    public readonly email: AlertEmail,
    public readonly sms: AlertSMS,
    public readonly sensorIds: string[],
    public readonly organizationId: string,
    public readonly repeat: number,
    public readonly delayedNotification: number,
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date(),
    public readonly recipients?: Recipient[]
  ) {}

  static create(
    id: string,
    name: string,
    organizationId: string,
    enabled: boolean = false,
    conditions: AlertCondition[] = [],
    sensorIds: string[] = [],
    repeat: number = 1,
    delayedNotification: number = 0
  ): Alert {
    return new Alert(
      id,
      name,
      enabled,
      conditions,
      { enabled: false, emails: [] },
      { enabled: false, phoneNumbers: [] },
      sensorIds,
      organizationId,
      repeat,
      delayedNotification
    );
  }

  isActive(): boolean {
    return this.enabled && this.conditions.length > 0;
  }

  hasEmailNotification(): boolean {
    return this.email.enabled && this.email.emails.length > 0;
  }

  hasSMSNotification(): boolean {
    return this.sms.enabled && this.sms.phoneNumbers.length > 0;
  }

  updateConditions(conditions: AlertCondition[]): Alert {
    return new Alert(
      this.id,
      this.name,
      this.enabled,
      conditions,
      this.email,
      this.sms,
      this.sensorIds,
      this.organizationId,
      this.repeat,
      this.delayedNotification,
      this.createdAt,
      new Date(),
      this.recipients
    );
  }

  toggleEnabled(): Alert {
    return new Alert(
      this.id,
      this.name,
      !this.enabled,
      this.conditions,
      this.email,
      this.sms,
      this.sensorIds,
      this.organizationId,
      this.repeat,
      this.delayedNotification,
      this.createdAt,
      new Date(),
      this.recipients
    );
  }

  static deriveEmailsAndPhones(recipients: Recipient[]): { emails: string[]; phoneNumbers: string[] } {
    const emails: string[] = [];
    const phoneNumbers: string[] = [];
    for (const r of recipients) {
      if (r.viaEmail && r.email) emails.push(r.email);
      if (r.viaSms && r.phone) phoneNumbers.push(r.phone);
    }
    return { emails, phoneNumbers };
  }

  static buildRecipientsFromLegacy(
    emails: string[],
    phoneNumbers: string[],
    addressBook: AddressBookEntry[]
  ): Recipient[] {
    const byContactId = new Map<string, Recipient>();
    const adHocRecipients: Recipient[] = [];

    for (const email of emails) {
      const match = addressBook.find(e => e.email === email);
      if (match) {
        const existing = byContactId.get(match.id);
        if (existing) {
          byContactId.set(match.id, new Recipient({ ...existing.toJSON(), viaEmail: true }));
        } else {
          byContactId.set(match.id, Recipient.fromAddressBookEntry(match, true, false));
        }
      } else {
        adHocRecipients.push(Recipient.fromAdHoc({
          name: email,
          email,
          viaEmail: true,
          viaSms: false
        }));
      }
    }

    for (const phone of phoneNumbers) {
      const match = addressBook.find(e => e.phone === phone);
      if (match) {
        const existing = byContactId.get(match.id);
        if (existing) {
          byContactId.set(match.id, new Recipient({ ...existing.toJSON(), viaSms: true }));
        } else {
          byContactId.set(match.id, Recipient.fromAddressBookEntry(match, false, true));
        }
      } else {
        adHocRecipients.push(Recipient.fromAdHoc({
          name: phone,
          phone,
          viaEmail: false,
          viaSms: true
        }));
      }
    }

    return [...byContactId.values(), ...adHocRecipients];
  }

  static mergeChannelRecipients(
    current: Recipient[],
    nextForChannel: Recipient[],
    channel: 'email' | 'sms'
  ): Recipient[] {
    const flagKey: 'viaEmail' | 'viaSms' = channel === 'email' ? 'viaEmail' : 'viaSms';
    const otherFlagKey: 'viaEmail' | 'viaSms' = channel === 'email' ? 'viaSms' : 'viaEmail';

    const keyOf = (r: Recipient): string =>
      r.contactId ?? (channel === 'email' ? (r.email ?? '') : (r.phone ?? ''));
    const nextKeys = new Set(nextForChannel.map(keyOf));
    const result: Recipient[] = [];
    const handled = new Set<string>();

    for (const r of current) {
      const key = keyOf(r);
      handled.add(key);
      if (r[flagKey]) {
        if (nextKeys.has(key)) {
          result.push(r);
        } else if (r[otherFlagKey]) {
          result.push(new Recipient({ ...r.toJSON(), [flagKey]: false }));
        }
      } else if (nextKeys.has(key)) {
        result.push(new Recipient({ ...r.toJSON(), [flagKey]: true }));
      } else {
        result.push(r);
      }
    }

    for (const r of nextForChannel) {
      if (!handled.has(keyOf(r))) {
        result.push(r);
      }
    }

    return result;
  }
}
